// =============================================================================
// FILE: packages/renderer/src/lib/capture/qaqc.ts
// PURPOSE: R2 QA/QC Intelligence + Orphan Reference Detector. Deterministic,
//   offline analysis of captured items — detects design topics, missing
//   specifications for referenced technologies, and orphan references (terms
//   mentioned but never supported). Foundation for a future knowledge-graph
//   / AI-backed engine; the analyze() contract stays stable.
// =============================================================================

import type { CaptureItem } from '../../stores/capture.store.js';

export interface QAFinding { severity: 'error' | 'warn' | 'info'; term: string; message: string; missing?: string[] }
export interface QAReport { topics: string[]; findings: QAFinding[]; orphans: string[]; itemCount: number }

const TOPIC_KEYWORDS: Record<string, string[]> = {
  'Fiber backbone': ['fiber backbone', 'backbone', 'om4', 'om3', 'os2', 'singlemode', 'multimode'],
  'IDF / MDF locations': ['idf', 'mdf', 'telecom room', 'equipment room'],
  'Pathway routing': ['pathway', 'conduit', 'cable tray', 'j-hook', 'raceway'],
  'Owner standards': ['owner standard', 'bicsi', 'tia', 'ansi', 'standard'],
  'Grounding & bonding': ['grounding', 'bonding', 'tmgb', 'tgb'],
  'Testing & certification': ['test', 'certification', 'otdr', 'fluke'],
  'Network / VLAN': ['vlan', 'ip plan', 'subnet', 'switch'],
};

// Referenced technology → companion specs that SHOULD accompany it.
const SPEC_RULES: { term: string; label: string; requires: { key: string; label: string }[] }[] = [
  { term: 'fiber backbone', label: 'Fiber backbone', requires: [
    { key: 'count', label: 'Fiber count' }, { key: 'pathway', label: 'Pathway specification' },
    { key: 'termination', label: 'Termination requirements' }, { key: 'test', label: 'Testing requirements' }] },
  { term: 'om4', label: 'OM4 fiber', requires: [
    { key: 'count', label: 'Fiber count' }, { key: 'distance', label: 'Distance limitation' },
    { key: 'termination', label: 'Termination requirements' }, { key: 'test', label: 'Testing requirements' },
    { key: 'standard', label: 'Standards reference' }] },
  { term: 'connectivity', label: 'Building connectivity', requires: [
    { key: 'pathway', label: 'Pathway specification' }, { key: 'count', label: 'Fiber count' },
    { key: 'backbone', label: 'Backbone architecture' }] },
];
const REQ_SYNONYMS: Record<string, string[]> = {
  count: ['fiber count', 'strand', 'count', 'no. of fibers', 'number of fibers'],
  pathway: ['pathway', 'conduit', 'cable tray', 'raceway', 'innerduct'],
  termination: ['termination', 'terminate', 'connector', 'lc', 'sc', 'patch panel'],
  test: ['test', 'testing', 'otdr', 'certification'],
  distance: ['distance', 'length', 'meter', 'metre', 'ft', 'feet'],
  standard: ['tia', 'ansi', 'bicsi', 'iso', 'standard'],
  backbone: ['backbone', 'riser', 'inter-building'],
};

// Terms that, if mentioned once with no supporting context, are likely orphans.
const ORPHAN_CANDIDATES: { term: string; support: string[] }[] = [
  { term: 'security vlan', support: ['ip plan', 'network diagram', 'subnet', 'switch', 'firewall'] },
  { term: 'voice vlan', support: ['ip plan', 'sip', 'voip', 'switch'] },
  { term: 'poe', support: ['watt', 'budget', 'switch', 'power'] },
];

function has(text: string, needle: string): boolean { return text.includes(needle); }
function count(text: string, needle: string): number { return text.split(needle).length - 1; }

export function analyzeCapture(items: CaptureItem[]): QAReport {
  const text = items.map((i) => `${i.content} ${i.keywords.join(' ')}`).join('  \n  ').toLowerCase();
  const topics = Object.entries(TOPIC_KEYWORDS)
    .filter(([, kws]) => kws.some((k) => has(text, k)))
    .map(([topic]) => topic);

  const findings: QAFinding[] = [];
  for (const rule of SPEC_RULES) {
    if (!has(text, rule.term)) continue;
    const missing = rule.requires
      .filter((r) => !(REQ_SYNONYMS[r.key] ?? [r.key]).some((syn) => has(text, syn)))
      .map((r) => r.label);
    if (missing.length) {
      findings.push({ severity: 'error', term: rule.label, message: `"${rule.label}" referenced but supporting specifications are missing.`, missing });
    } else {
      findings.push({ severity: 'info', term: rule.label, message: `"${rule.label}" is fully specified.` });
    }
  }

  const orphans: string[] = [];
  for (const o of ORPHAN_CANDIDATES) {
    if (count(text, o.term) === 1 && !o.support.some((s) => has(text, s))) {
      orphans.push(o.term);
      findings.push({ severity: 'warn', term: o.term, message: `Potential orphan reference: "${o.term}" appears once with no supporting documentation. Recommend verification.` });
    }
  }

  if (!findings.length) findings.push({ severity: 'info', term: 'General', message: 'No missing specifications or orphan references detected in the captured set.' });
  return { topics, findings, orphans, itemCount: items.length };
}

// Lightweight keyword extraction for silent metadata capture.
export function extractKeywords(content: string): string[] {
  const known = ['fiber', 'backbone', 'idf', 'mdf', 'om4', 'om3', 'os2', 'vlan', 'pathway', 'conduit', 'cable tray', 'grounding', 'bicsi', 'tia', 'poe', 'switch', 'patch panel', 'riser', 'connectivity', 'termination', 'testing'];
  const lower = content.toLowerCase();
  return known.filter((k) => lower.includes(k));
}
